import {Component, inject} from '@angular/core';
import {FmsInvoiceGridService} from "../../service/fms-invoice-grid.service";
import {GridEventOutput, GridModel} from "../../../shared/model/page-grid";
import {FmsInvoiceModel, FmsInvoiceResponse} from "../../model/fms-invoice-model";
import {PageGridComponent} from "../../../shared/components/page-grid/page-grid.component";
import {DatePipe} from "@angular/common";
import {MatDialog} from "@angular/material/dialog";
import {TripDetailsModalComponent} from "../../../trip/components/trip-details-modal/trip-details-modal.component";
import {
  AdxInvoiceDetailsModalComponent
} from "../../../adx-invoice/components/adx-invoice-details-modal/adx-invoice-details-modal.component";
import {of} from "rxjs";
import {GeneralLedgerService} from "../../../general-ledger/service/general-ledger.service";
import {TramsDataSourceService} from "../../../payments/service/trams-data-source.service";
import {TramsDataSourceModel} from "../../../payments/models/trams-data-source";
import {
  ExternalTravelServiceTypeModel,
  TravelTypeModel
} from "../../../general-ledger/model/general-ledger";

@Component({
    selector: 'app-fms-invoices',
    imports: [
        PageGridComponent
    ],
    templateUrl: './fms-invoices.component.html'
})
export class FmsInvoicesComponent {
  private readonly fmsInvoiceGridService = inject(FmsInvoiceGridService);
  private readonly datePipe = inject(DatePipe);
  private readonly dialog = inject(MatDialog);
  private readonly generalLedgerService = inject(GeneralLedgerService);
  private readonly tramsDataSourceService = inject(TramsDataSourceService);

  /** Codes stored in AdxInvoice.InvoiceTypeCode, per the InvoiceTypes enum. */
  private readonly invoiceTypeOptions$ = of([
    {code: 'S', name: 'Sale'},
    {code: 'R', name: 'Refund'},
    {code: 'A', name: 'Adjustment'},
    {code: 'O', name: 'Spoiled'},
    {code: 'V', name: 'Voucher'},
  ]);

  protected readonly gridConfig: GridModel<FmsInvoiceResponse> = {
    gridDataTestId: 'fms-invoices-list',
    gridTitle: 'Fms Invoices',
    replayButton: true,
    isTotalsRowHidden: true,
    defaultSort: {active: 'dateUpdated', direction: 'desc'},
    columns: [
      {
        columnProperty: 'invoiceNumber',
        columnHeader: 'Invoice Number',
        sort: true,
        clickable: true,
        linkEventFunction: (item: FmsInvoiceModel) => `AdxInvoices/${item.invoiceNumber}`,
      },
      {
        columnProperty: 'tripNumber',
        columnHeader: 'Trip Number',
        sort: true,
        clickable: true,
        linkEventFunction: (item: FmsInvoiceModel) => `Trip/${item.tripNumber}`,
      },
      {
        columnProperty: 'clientName',
        columnHeader: 'Client Name',
        sort: true,
      },
      {
        columnProperty: 'invoiceRecordLocator',
        columnHeader: 'Invoice Record Locator',
        sort: true,
      },
      {
        columnProperty: 'invoiceType',
        columnHeader: 'Invoice Type',
        sort: true,
      },
      {
        columnProperty: 'issueDate',
        columnHeader: 'Issue Date',
        sort: true,
        processData: (date: string) => this.datePipe.transform(date, 'MM/dd/yyyy') ?? '',
      },
      {
        columnProperty: 'dataSourceName',
        columnHeader: 'Data Source Name',
        sort: true,
      },
      {
        columnProperty: 'branchNumber',
        columnHeader: 'Branch Number',
        sort: true,
      },
      {
        columnProperty: 'invoiceGroup',
        columnHeader: 'Invoice Group',
        sort: true,
      },
      {
        columnProperty: 'isSyncedToErp',
        columnHeader: 'ERP Sync',
        sort: false,
        processData: (item: any) => item === true ? 'Synced' : 'Not Synced',
      },
      {
        columnProperty: 'erpSalesOrderNumber',
        columnHeader: 'ERP Sales Order Number',
        sort: false,
      }
    ],
    filters: [
      {
        filterProperty: 'invoiceNumber',
        filterDisplayName: 'Invoice Number',
        filterType: 'TEXT',
      },
      {
        filterProperty: 'invoiceRecordLocator',
        filterDisplayName: 'Invoice Record Locator',
        filterType: 'TEXT',
      },
      {
        filterProperty: 'invoiceType',
        filterDisplayName: 'Invoice Type',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.invoiceTypeOptions$,
          dropdownValueProp: 'code',
          dropdownDisplayWith: (item: {code: string; name: string}) => item.name
        }
      },
      {
        filterProperty: 'invoiceGroup',
        filterDisplayName: 'Invoice Group',
        filterType: 'TEXT',
      },
      {
        filterProperty: 'tripNumber',
        filterDisplayName: 'Trip Number',
        filterType: 'TEXT',
      },
      {
        filterProperty: 'branchNumber',
        filterDisplayName: 'Branch Number',
        filterType: 'TEXT',
      },
      {
        filterProperty: 'issueDateFrom',
        filterDisplayName: 'Issue Date From',
        filterType: 'DATE',
        validateDates: {
          propertyFrom: 'issueDateFrom',
          propertyTo: 'issueDateTo',
        }
      },
      {
        filterProperty: 'issueDateTo',
        filterDisplayName: 'Issue Date To',
        filterType: 'DATE',
        validateDates: {
          propertyFrom: 'issueDateFrom',
          propertyTo: 'issueDateTo',
        }
      },
      {
        filterProperty: 'travelTypeId',
        filterDisplayName: 'Travel Type',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.generalLedgerService.getTravelTypeList(),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: TravelTypeModel) => item.travelTypeName
        }
      },
      {
        filterProperty: 'externalTravelServiceTypeId',
        filterDisplayName: 'External Travel Service Type',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.generalLedgerService.getExternalTravelServiceTypeList(),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: ExternalTravelServiceTypeModel) => item.name
        }
      },
      {
        filterProperty: 'dataSourceId',
        filterDisplayName: 'Data Source',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.tramsDataSourceService.getTramsDataSourceList(),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: TramsDataSourceModel) => this.dataSourceLabel(item)
        }
      },
      {
        filterProperty: 'clientName',
        filterDisplayName: 'Client',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Client',
          autocompleteColumnName: 'ProfileName',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'vendorName',
        filterDisplayName: 'Vendor Name',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Vendor',
          autocompleteColumnName: 'VendorName',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'agentName',
        filterDisplayName: 'Agent Name',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Agent',
          autocompleteColumnName: 'AgentName',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'isMor',
        filterDisplayName: 'MOR',
        filterType: 'RADIOBUTTON',
        config: {
          radioButtonBoolean: true
        }
      },
      {
        filterProperty: 'isSyncedToErp',
        filterDisplayName: 'Synced to ERP',
        filterType: 'RADIOBUTTON',
        config: {
          radioButtonBoolean: true
        }
      },
    ],
    gridGetService: this.fmsInvoiceGridService,
  };

  /**
   * Some sources carry a SourceName that is just the id again, which would read "102 (102)".
   */
  private dataSourceLabel(dataSource: TramsDataSourceModel): string {
    const name = dataSource.sourceName?.trim();
    return !name || name === String(dataSource.id) ? `${dataSource.id}` : `${name} (${dataSource.id})`;
  }

  private openInvoiceDialog(item: FmsInvoiceModel): void {
    this.dialog.open(AdxInvoiceDetailsModalComponent, {
      width: '55%',
      data: { adxInvoiceId: item.invoiceNumber, title: 'Invoice Information' }
    });
  }

  private openTripDialog(item: FmsInvoiceModel): void {
    const dialog = this.dialog.open(TripDetailsModalComponent, {width: '55%'});
    dialog.componentInstance.tripId.set(item.tripNumber);
  }

  protected handleItemClick({column, item}: GridEventOutput<FmsInvoiceModel>): void {
    if (column === 'invoiceNumber') {
      this.openInvoiceDialog(item);
    }
    if (column === 'tripNumber') {
      this.openTripDialog(item);
    }
  }

}
